// ============================================================
// API Client - centralized fetch wrapper with auth
// ============================================================

import { useAuthStore } from '../stores/authStore';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface FetchOptions extends RequestInit {
    skipAuth?: boolean;
}

async function apiFetch<T = any>(path: string, options: FetchOptions = {}): Promise<T> {
    const { skipAuth, ...fetchOptions } = options;
    const { accessToken, refreshAccessToken, logout } = useAuthStore.getState();

    const headers: Record<string, string> = {
        ...(fetchOptions.headers as Record<string, string> || {}),
    };

    // Don't set Content-Type for FormData (let browser set multipart boundary)
    if (!(fetchOptions.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    if (!skipAuth && accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
        ...fetchOptions,
        headers,
    });

    // Handle 401 - try refresh
    if (response.status === 401 && !skipAuth && accessToken) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
            headers['Authorization'] = `Bearer ${useAuthStore.getState().accessToken}`;
            const retryResponse = await fetch(`${API_BASE}${path}`, { ...fetchOptions, headers });
            if (retryResponse.ok) {
                return retryResponse.json();
            }
        }
        logout();
        throw new Error('Session expired');
    }

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || `Request failed: ${response.status}`);
    }

    return data;
}

// Convenience methods
export const api = {
    get: <T = any>(path: string, opts?: FetchOptions) =>
        apiFetch<T>(path, { method: 'GET', ...opts }),

    post: <T = any>(path: string, body?: any, opts?: FetchOptions) =>
        apiFetch<T>(path, {
            method: 'POST',
            body: body instanceof FormData ? body : JSON.stringify(body),
            ...opts,
        }),

    put: <T = any>(path: string, body?: any, opts?: FetchOptions) =>
        apiFetch<T>(path, {
            method: 'PUT',
            body: body instanceof FormData ? body : JSON.stringify(body),
            ...opts,
        }),

    delete: <T = any>(path: string, opts?: FetchOptions) =>
        apiFetch<T>(path, { method: 'DELETE', ...opts }),
};
